import {
  ConversationPane,
  type ForumRequest,
  readConversationPaneRequest,
  withoutConversationPaneRequest,
} from "messaging/conversations/conversation-links";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router";

export type ConversationPaneRequestState = {
  /**
   * Set while the URL asks to open `pane`, and different for each navigation that asks: a second
   * click on the same link is a new request, and opens a pane the reader closed in between.
   */
  requestKey?: string;
  /** The conversation the URL asks the pane to open on, when it names one. */
  forumRequest?: ForumRequest;
  /**
   * Takes the request out of the URL, keeping everything else in it, so a reload shows the page as
   * the reader left it. Does nothing when there is no request.
   */
  forgetRequest: () => void;
};

/** What the page's URL asks of its conversation `pane` (#5826). See `conversation-links`. */
export default function useConversationPaneRequest(pane: ConversationPane): ConversationPaneRequestState {
  const location = useLocation();
  const navigate = useNavigate();
  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const request = readConversationPaneRequest(search, pane);

  return {
    requestKey: request ? location.key : undefined,
    forumRequest: request?.forum ? { forum: request.forum, key: location.key } : undefined,
    forgetRequest: () => {
      if (!request) return;
      navigate(`${location.pathname}${withoutConversationPaneRequest(search)}`, { replace: true });
    },
  };
}
